/*
 * Manage Inventory Form
 * Edit inventory items
 */

import * as React from 'react';

import {
    AbstractControl,
    FieldConfig,
    FormGenerator,
    FormGroup
} from 'react-reactive-form';
import { Button, Col, ListGroup, Row, Well } from 'react-bootstrap';
import {
    Ioption,
    IMaterial,
    IproductInfo,
    IproductQueueObject,
    IsearchNewProductInstallBatchMode,
    Iuser
} from '../../models';
import Axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, filter, isEmpty, map } from 'lodash';
import {
    getProducts,
    resetNewProducts,
    setInstallBatchMode,
    setProductSearchFormValues,
    setRememberProductSearchFilters,
    setRememberedProductSearchFilters,
    toggleEditProductModal,
    updateProductSearchFormValues,
    updateRememberedProductSearchFilters
} from '../../actions/manageInventoryActions';

import { FormUtil } from '../common/FormUtil';
import { IsearchProductFormValues, ProductAttributes } from '../../modelsForms';
import { TFunction } from 'i18next';
import { constants } from '../../constants/constants';
import { toastr } from 'react-redux-toastr';
import { getMergeableProducts } from '../../actions/manageProductQueueActions';

interface Iprops {
    toggleModal: () => void;
    toggleEditProductModal: typeof toggleEditProductModal;
    selectedItem: IMaterial;
    loading: boolean;
    colorButton: string;
    t: TFunction;
    productInfo: IproductInfo;
    formValues: Partial<IsearchProductFormValues>;
    getProducts: typeof getProducts;
    getMergeableProducts: typeof getMergeableProducts;
    newProducts: (IMaterial | IproductQueueObject)[];
    resetNewProducts: typeof resetNewProducts;
    handleProductSelect?: (
        product: IMaterial,
        installBatchMode: IsearchNewProductInstallBatchMode
    ) => void;
    handleMergeProduct: (product: IMaterial) => void;
    isApproved?: boolean;
    setProductSearchFormValues: typeof setProductSearchFormValues;
    updateProductSearchFormValues: typeof updateProductSearchFormValues;
    enableMergeProductMode: boolean;
    disableCreateNew: boolean;
    user: Iuser;
    isProductQueue?: boolean;
    installBatchMode: IsearchNewProductInstallBatchMode;
    setInstallBatchMode: typeof setInstallBatchMode;
    rememberFilters: boolean;
    rememberedFilters: Partial<IsearchProductFormValues>;
    setRememberProductSearchFilters: typeof setRememberProductSearchFilters;
    setRememberedProductSearchFilters: typeof setRememberedProductSearchFilters;
    updateRememberedProductSearchFilters: typeof updateRememberedProductSearchFilters;
}

interface Istate {
    fieldConfig: FieldConfig;
}

class SearchNewProductsForm extends React.Component<Iprops, Istate> {
    private formGroup: FormGroup | any;
    private subscription: any;
    private handleSubmitDebounced: () => void;
    private cancelProductsFetch: (() => void) | null = null;

    constructor(props: Iprops) {
        super(props);
        this.handleSubmitDebounced = debounce(
            this.handleSubmit,
            constants.formDebounceTime
        );
        this.state = {
            fieldConfig: this.buildFieldConfig()
        };
    }

    componentDidMount() {
        if (this.props.enableMergeProductMode) {
            const selectedFormValues: Partial<IsearchProductFormValues> = this.buildFormValuesFromSelected();
            const productAttributes = this.getProductAttributes(
                selectedFormValues
            );

            this.setState({
                fieldConfig: this.buildFieldConfig(selectedFormValues)
            });
            this.props.setProductSearchFormValues(selectedFormValues);

            const selectedProductStandard = this.props.productInfo.standardOptions
                .filter((value: Ioption) => {
                    return this.props.user.userStandards.includes(value.value);
                })
                .map((value: Ioption) => value.value);

            if (this.props.isProductQueue)
                this.props.getMergeableProducts({
                    page: 1,
                    search: selectedFormValues.search || '',
                    mainCategoryID: productAttributes.mainCategoryID || '',
                    subCategoryID: productAttributes.subcategoryID || '',
                    brandID: productAttributes.brandID || '',
                    origin: productAttributes.origin || '',
                    isFinalProduct: selectedFormValues.isFinalProduct || false,
                    standards: selectedProductStandard || [],
                    isApproved: true
                });
            else
                this.props.getProducts(
                    1,
                    '',
                    true,
                    productAttributes,
                    selectedProductStandard,
                    this.props.isApproved
                );
        } else if (
            this.canRememberFilters() &&
            this.props.rememberFilters &&
            !isEmpty(this.props.rememberedFilters)
        ) {
            this.restoreRememberedFilters();
        } else {
            this.props.resetNewProducts();
            this.props.setProductSearchFormValues({});
        }
    }
    componentWillUnmount() {
        if (this.cancelProductsFetch) {
            this.cancelProductsFetch();
        }
        if (
            this.canRememberFilters() &&
            this.props.rememberFilters &&
            this.formGroup
        ) {
            this.props.setRememberedProductSearchFilters(
                this.getFilterSnapshot()
            );
        }
        this.props.setProductSearchFormValues({ isFinalProduct: true });
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.props.setInstallBatchMode({
            installBaseIDs: [],
            isBatchMode: false
        });
    }
    componentDidUpdate(prevProps: Iprops) {
        if (
            this.props.formValues.mainCategoryID !==
            prevProps.formValues.mainCategoryID
        ) {
            this.setState({ fieldConfig: this.buildFieldConfig() });
        }
    }
    /*
     * Clear Selections (MMG-4169)
     * Empties every slicer/filter input at once and rebuilds the form.
     * standardID is set to an empty array (not undefined) so buildFieldConfig
     * does not repopulate the user's default standards.
     */
    clearSelections = () => {
        const defaults: Partial<IsearchProductFormValues> = {
            mainCategoryID: undefined,
            subcategoryID: undefined,
            search: '',
            brandID: undefined,
            standardID: [] as any,
            isFinalProduct: true
        };
        this.props.setProductSearchFormValues(defaults);
        if (this.canRememberFilters() && this.props.rememberFilters) {
            this.props.setRememberedProductSearchFilters(defaults);
        }
        this.setState({ fieldConfig: this.buildFieldConfig(defaults) });
        this.props.resetNewProducts();
    };

    /*
     * Remember Filter Selections (MMG-4170)
     * Only offered in the plain product search; merge mode, product queue,
     * batch mode and locked-product contexts never read or write the
     * remembered filters.
     */
    canRememberFilters = (): boolean => {
        return (
            this.props.enableMergeProductMode === false &&
            !this.props.isProductQueue &&
            !this.props.installBatchMode?.isBatchMode &&
            this.props.selectedItem.id.length === 0
        );
    };

    // only the filter controls; the remember toggle itself is not a filter
    getFilterSnapshot = (): Partial<IsearchProductFormValues> => {
        const {
            mainCategoryID,
            subcategoryID,
            search,
            brandID,
            standardID,
            isFinalProduct
        } = this.formGroup.value;
        return {
            mainCategoryID,
            subcategoryID,
            search,
            brandID,
            standardID,
            isFinalProduct
        };
    };

    /*
     * Re-applies the remembered filters instead of the defaults and re-runs
     * the search so the results match the restored filters.
     */
    restoreRememberedFilters = () => {
        const remembered = { ...this.props.rememberedFilters };
        // remembered-but-blank standards fall back to the user's default
        // standards on the next open, same as a fresh flyout
        if (isEmpty(remembered.standardID)) {
            remembered.standardID = this.props.productInfo.standardOptions.filter(
                (option: Ioption) =>
                    this.props.user.userStandards.includes(option.value)
            ) as any;
        }
        this.props.setProductSearchFormValues(remembered);
        this.setState({ fieldConfig: this.buildFieldConfig(remembered) });
        const productAttributes = this.getProductAttributes(remembered);
        const rememberedStandards: Ioption[] =
            (remembered.standardID as any) || [];
        const source = Axios.CancelToken.source();
        this.cancelProductsFetch = source.cancel;
        this.props.getProducts(
            1,
            remembered.search || '',
            remembered.isFinalProduct ?? true,
            productAttributes,
            rememberedStandards.map(standard => standard.value),
            this.props.isApproved,
            source.token
        );
    };

    buildFormValuesFromSelected = (): Partial<IsearchProductFormValues> => {
        if (this.props.selectedItem.id.length) {
            const { selectedItem, productInfo } = this.props;

            const { subcategoryID } = selectedItem;
            const subcategory = productInfo.subcategories[subcategoryID];
            const mainCategory =
                productInfo.mainCategories[subcategory.mainCategoryID];
            const mainCategoryOption =
                FormUtil.convertToSingleOption(mainCategory) || undefined;

            return {
                subcategoryID: undefined, // Don't pre-populate subcategory, even if product has one
                mainCategoryID: mainCategoryOption,
                search: ''
            };
        } else {
            return {
                subcategoryID: undefined,
                mainCategoryID: undefined,
                search: undefined,
                isFinalProduct: true
            };
        }
    };
    buildFieldConfig = (
        defaultFormValues: Partial<IsearchProductFormValues> = this.props
            .formValues
    ) => {
        const disabled = false;
        const { selectedItem } = this.props;
        const {
            subcategories,
            mainCategoryOptions,
            brandOptions,
            brands,
            standardOptions
        } = this.props.productInfo;
        const {
            search,
            brandID,
            isFinalProduct,
            standardID
        } = defaultFormValues;

        let filteredSubCategoryOptions: Ioption[] = [];
        let selectedSubCategory = null;
        let selectedBrand = null;
        let selectedProductStandard = null;
        let subcategoryID: string | undefined = undefined;
        let mainCategoryID: string | undefined = undefined;

        // If selectedItem is set, that means we are updating an existing InstallBase's product, main category will be locked.
        if (selectedItem.id.length > 0) {
            subcategoryID = selectedItem.subcategoryID;
            mainCategoryID = subcategories[subcategoryID]?.mainCategoryID;
        } else {
            subcategoryID = defaultFormValues?.subcategoryID?.value;
            mainCategoryID = defaultFormValues?.mainCategoryID?.value;
        }

        // For merge product mode (Search and Replace), don't pre-populate subcategory from selectedItem,
        // but preserve whatever the user has selected in defaultFormValues
        if (this.props.enableMergeProductMode && selectedItem.id.length > 0) {
            const subVal = defaultFormValues?.subcategoryID;
            subcategoryID =
                (typeof subVal === 'string' ? subVal : subVal?.value) ||
                undefined;
        }

        if (mainCategoryID) {
            filteredSubCategoryOptions = FormUtil.convertToOptions(
                filter(
                    subcategories,
                    sub => sub.mainCategoryID === mainCategoryID
                )
            );
            if (subcategoryID) {
                const subcategory = subcategories[subcategoryID];
                if (subcategory.mainCategoryID === mainCategoryID) {
                    selectedSubCategory =
                        filteredSubCategoryOptions.find(
                            opt => opt.value === subcategoryID
                        ) || null;
                }
            }
        } else if (this.props.formValues.mainCategoryID) {
            mainCategoryID = this.props.formValues.mainCategoryID.value;
        }

        if (brandID) {
            const brand = brands[brandID.value];
            selectedBrand = FormUtil.convertToSingleOption(brand);
        }
        if (standardID !== null && standardID !== undefined) {
            selectedProductStandard = standardID;
        } else if (standardID === undefined) {
            selectedProductStandard = standardOptions.filter(
                (value: Ioption) => {
                    return this.props.user.userStandards.includes(value.value);
                }
            );
        }

        const selectedMainCategoryOption = mainCategoryOptions.find(
            category => category.value === mainCategoryID
        );

        const fieldConfigControls = {
            mainCategoryID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: mainCategoryOptions,
                    label: 'common:mainCategory',
                    colWidth: 6,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    name: 'product-group'
                },
                formState: {
                    value: selectedMainCategoryOption || undefined,
                    disabled:
                        this.props.enableMergeProductMode ||
                        this.props.installBatchMode?.isBatchMode
                }
            },
            search: {
                render: FormUtil.TextInputWithoutValidation,
                meta: {
                    label: 'search',
                    colWidth: 6,
                    type: 'input',
                    placeholder: 'searchByName',
                    name: 'product-search'
                },
                formState: { value: search, disabled }
            },
            subcategoryID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: filteredSubCategoryOptions,
                    label: 'common:subCategory',
                    colWidth: 6,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    name: 'subcategory',
                    isClearable: true
                },
                formState: {
                    value: selectedSubCategory,
                    disabled
                }
            },
            brandID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: brandOptions,
                    label: 'common:manufacturer',
                    colWidth: 6,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: false,
                    name: 'manufacturer',
                    isClearable: true
                },
                formState: {
                    value: selectedBrand,
                    disabled
                }
            },
            standardID: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: standardOptions,
                    label: 'productForm.standards',
                    colWidth: 12,
                    placeholder: 'common:searchPlaceholder',
                    isMulti: true,
                    name: 'productStandard',
                    isClearable: true
                },
                formState: {
                    value: selectedProductStandard,
                    disabled
                }
            },
            isFinalProduct: {
                render: FormUtil.Toggle,
                meta: {
                    label: 'productForm.EndProduct',
                    colWidth: 4,
                    style: { marginTop: '8px', whiteSpace: 'nowrap' as const }
                },
                formState: { value: isFinalProduct ?? true, disabled }
            },
            ...(this.canRememberFilters()
                ? {
                      rememberFilters: {
                          render: FormUtil.Toggle,
                          meta: {
                              label: 'rememberFilterSelections',
                              colWidth: 8,
                              style: {
                                  marginTop: '8px',
                                  textAlign: 'right' as const,
                                  whiteSpace: 'nowrap' as const
                              },
                              name: 'remember-filters'
                          },
                          formState: {
                              value: this.props.rememberFilters,
                              disabled
                          }
                      }
                  }
                : {})
        };
        return FormUtil.translateForm(
            {
                controls: { ...fieldConfigControls }
            },
            this.props.t
        );
    };

    /*
     * (reusable)
     * subscribe to the formGroup changes
     */
    subscribeToChanges = () => {
        for (const key in this.formGroup.controls) {
            if (this.formGroup.controls.hasOwnProperty(key)) {
                this.subscription = this.formGroup
                    .get(key)
                    .valueChanges.subscribe((value: any) => {
                        this.onValueChanges(value, key);
                    });
            }
        }
    };

    /*
     * (reusable)
     * set the table filters to redux on each value change
     */
    onValueChanges = (value: any, key: string) => {
        switch (key) {
            case 'rememberFilters':
                this.props.setRememberProductSearchFilters(value === true);
                if (value === true && this.formGroup) {
                    this.props.setRememberedProductSearchFilters(
                        this.getFilterSnapshot()
                    );
                }
                break;
            case 'mainCategoryID':
                this.props.updateProductSearchFormValues({
                    [key]: value,
                    subcategoryID: null
                });
                this.mirrorToRememberedFilters({
                    [key]: value,
                    subcategoryID: null
                });
                this.handleSubmitDebounced();
                break;
            default:
                this.props.updateProductSearchFormValues({ [key]: value });
                this.mirrorToRememberedFilters({ [key]: value });
                this.handleSubmitDebounced();
                break;
        }
    };

    // while the remember toggle is on, filter changes update the remembered set
    mirrorToRememberedFilters = (formValues: { [key: string]: any }) => {
        if (this.canRememberFilters() && this.props.rememberFilters) {
            this.props.updateRememberedProductSearchFilters(formValues);
        }
    };
    handleSubmit = () => {
        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error(
                this.props.t('toastMessage:invalidFormSubmission'),
                '',
                constants.toastrError
            );
            return;
        }

        const productAttributes = this.getProductAttributes(
            this.formGroup.value
        );

        const standardID = this.formGroup.value.standardID;
        let selectedStandards = [];
        if (standardID && standardID.length > 0) {
            selectedStandards = standardID.map(
                (standard: Ioption) => standard.value
            );
        }

        if (this.cancelProductsFetch) {
            this.cancelProductsFetch();
        }

        if (this.props.isProductQueue) {
            this.props.getMergeableProducts({
                page: 1,
                search: this.formGroup.value.search || '',
                mainCategoryID: productAttributes.mainCategoryID || '',
                subCategoryID: productAttributes.subcategoryID || '',
                brandID: productAttributes.brandID || '',
                origin: productAttributes.origin || '',
                isFinalProduct: this.formGroup.value.isFinalProduct,
                standards: selectedStandards || [],
                isApproved: true
            });
        } else {
            const source = Axios.CancelToken.source();
            this.cancelProductsFetch = source.cancel;
            this.props.getProducts(
                1,
                this.formGroup.value.search,
                this.formGroup.value.isFinalProduct,
                productAttributes,
                selectedStandards,
                this.props.isApproved,
                source.token
            );
        }
    };

    getValue = (object?: Ioption | string): string => {
        return typeof object === 'string' ? object : object?.value ?? '';
    };

    getProductAttributes = (
        selectedFormValues: Partial<IsearchProductFormValues>
    ): ProductAttributes => {
        const keys: (keyof ProductAttributes)[] = [
            'mainCategoryID',
            'subcategoryID',
            'brandID',
            'origin',
            'powerID',
            'productTypeID',
            'systemSizeID'
        ];

        return keys.reduce((attributes: ProductAttributes, key) => {
            attributes[key as keyof ProductAttributes] = this.getValue(
                selectedFormValues[key]
            );
            return attributes;
        }, {} as ProductAttributes);
    };

    setForm = (form: AbstractControl) => {
        this.formGroup = form;
        this.formGroup.meta = {
            loading: this.props.loading
        };
        if (!this.subscription) {
            setTimeout(() => {
                this.subscribeToChanges();
            }, 300);
        }
    };

    render() {
        const {
            t,
            colorButton,
            loading,
            newProducts,
            selectedItem,
            enableMergeProductMode,
            disableCreateNew,
            handleProductSelect,
            handleMergeProduct,
            toggleModal
        } = this.props;

        const formClassName = `clearfix beacon-form search-products-form ${colorButton}`;

        let searchActive = false;
        if (
            !loading &&
            this.formGroup &&
            this.formGroup.value &&
            (this.formGroup.value.search || this.formGroup.value.mainCategoryID)
        ) {
            searchActive = true;
        }

        // "Clear Selection" stays in place to avoid layout shift; it is disabled
        // when no slicer is set so it can't be clicked with nothing to clear.
        const fv = (this.formGroup && this.formGroup.value) || {};
        const hasSelections = !!(
            fv.mainCategoryID ||
            fv.subcategoryID ||
            (fv.search && fv.search.length) ||
            fv.brandID ||
            (fv.standardID && fv.standardID.length) ||
            fv.isFinalProduct === false
        );

        const ProductListItem = ({
            product,
            sItem
        }: {
            product: IMaterial;
            sItem?: IMaterial;
        }) => {
            const className =
                sItem && sItem.id === product.id
                    ? 'list-group-item new-product-item selected'
                    : 'list-group-item new-product-item';
            return (
                <li
                    className={className}
                    onClick={() => {
                        if (
                            enableMergeProductMode === false &&
                            handleProductSelect
                        ) {
                            handleProductSelect(
                                product,
                                this.props.installBatchMode
                            );
                        } else {
                            handleMergeProduct(product);
                        }
                    }}
                >
                    <h4> {product.name} </h4>
                </li>
            );
        };

        return (
            <div>
                <div
                    className="search-products-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginLeft: '15px',
                        marginRight: '15px'
                    }}
                >
                    <p style={{ lineHeight: '1.5rem', margin: 0 }}>
                        {t('searchNewProductInstructions')}
                    </p>
                    <Button
                        type="button"
                        className="clear-selections-button"
                        onClick={this.clearSelections}
                        disabled={!hasSelections}
                    >
                        <span>{t('resetSelectionButton')}</span>
                        <FontAwesomeIcon icon={['far', 'times']} />
                    </Button>
                </div>

                <form
                    onSubmit={(e: React.MouseEvent<HTMLFormElement>) => {
                        e.preventDefault();
                        this.handleSubmit();
                    }}
                    className={formClassName}
                    style={{ display: 'block' }}
                >
                    <Row>
                        <Col xs={12}>
                            <FormGenerator
                                onMount={this.setForm}
                                fieldConfig={this.state.fieldConfig}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            {loading && (
                                <Well className="text-center">
                                    <i className="fa fa-spinner fa-spin" />{' '}
                                    {t('common:loadingText')}
                                </Well>
                            )}
                            {!loading && (
                                <>
                                    <ListGroup className="beacon-list-group">
                                        {map(newProducts, product => (
                                            <ProductListItem
                                                product={
                                                    'name' in product
                                                        ? product
                                                        : product.product
                                                }
                                                key={product.id}
                                                sItem={selectedItem}
                                            />
                                        ))}
                                    </ListGroup>
                                    {isEmpty(newProducts) && searchActive && (
                                        <Col xs={12}>
                                            <Well className="text-center">
                                                {t('noProductsFound')}
                                            </Well>
                                        </Col>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                    <Col xs={12} className="form-buttons text-right">
                        <Button
                            bsStyle="default"
                            type="button"
                            className="pull-left"
                            onClick={toggleModal}
                        >
                            {t('common:cancel')}
                        </Button>
                        {!disableCreateNew && enableMergeProductMode === false && (
                            <Button
                                bsStyle="link"
                                type="button"
                                disabled={loading}
                                className="right-side"
                                onClick={() => {
                                    this.props.toggleEditProductModal();
                                }}
                            >
                                {t('addNewProductButton')}
                            </Button>
                        )}

                        <button type="submit" style={{ display: 'none' }} />
                    </Col>
                </form>
            </div>
        );
    }
}
export default SearchNewProductsForm;
